import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Building2, CalendarDays, Check, CheckCircle2,
  ClipboardCheck, FileText, GitBranch, Sparkles, Timer, Users,
  ShieldCheck, Wallet, Search, Bell, MailPlus, Pencil,
} from "lucide-react";
import { Modal } from "../primitives/Modal";
import { useChainStore, cfBus, openAddContact, openAddProperty } from "../../data/chainStore";
import { initialsOf } from "../contacts/AddContactModal";
import { DealKickoffCelebration } from "./DealKickoffCelebration";
import { api, apiError, apiErrorCode, apiErrorField } from "../../lib/api";
import { PremiumInput } from "../brand/PremiumInput";
import { PremiumSelect } from "../brand/PremiumSelect";
import { PremiumTextarea } from "../brand/PremiumTextarea";

/* ════════════════════════════════════════════════════════════════════════
 * iter-270G — Create Deal Room flow refinement (pilot lock).
 *
 * 3-step flow:
 *   1. Property & Participants
 *   2. Transaction Details
 *   3. Create Deal Room
 *
 * Step 1 lays out the transaction property card + 5 canonical
 * participant cards (Seller · Buyer · Seller Solicitor · Buyer Solicitor
 * · Mortgage Broker) alongside a Transaction Snapshot rail with Launch
 * Readiness, a calm Workflow Preview and the "Your Deal Room Will
 * Include" summary. Every participant can be added later from the
 * Deal Room itself so the operator never gets stuck.
 * ════════════════════════════════════════════════════════════════════ */

const STEPS = [
  { id: "property",     label: "Property & Participants" },
  { id: "details",      label: "Transaction Details" },
  { id: "review",       label: "Create Deal Room" },
];

const Stepper = ({ current }) => {
  const idx = STEPS.findIndex((s) => s.id === current);
  return (
    /* iter-alignment · P — Trim the Stepper's vertical padding
     * (`pt-5 pb-3` → `pt-2 pb-2.5`) and the inner card padding
     * (`py-3` → `py-2`) so the Step 1 wizard fits within a
     * 1366×768 laptop viewport with no modal scroll.  The
     * negative `-mt-3` claws back the Modal body's default
     * top padding (`py-5 md:py-6`) on the wizard surface
     * only. */
    <div className="px-7 pt-2 pb-2.5 -mt-3">
      <div className="rounded-2xl bg-slate-50/70 ring-1 ring-slate-200/80 px-4 py-2">
        <div className="grid grid-cols-3 gap-3">
          {STEPS.map((s, i) => {
            const done = i < idx;
            const active = i === idx;
            return (
              <div key={s.id} className="flex items-center gap-2.5 min-w-0" data-testid={`wizard-step-${s.id}`}>
                <div
                  className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center transition-colors shrink-0 ${
                    active
                      ? "bg-indigo-600 text-white shadow-sm"
                      : done
                        ? "bg-indigo-600 text-white"
                        : "bg-white ring-1 ring-slate-200 text-slate-400"
                  }`}
                >
                  {done ? <Check size={12} strokeWidth={3} /> : i + 1}
                </div>
                <span
                  className={`text-[12px] truncate ${
                    active
                      ? "text-indigo-700 font-bold"
                      : done
                        ? "text-slate-700 font-semibold"
                        : "text-slate-500 font-semibold"
                  }`}
                >
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ─── Transaction Property card (auto-populated) ─────────────────── */
const TransactionPropertyCard = ({ property, sellerName, ownerName, form }) => {
  const isLeasehold = form.tenure === "leasehold";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="wizard-transaction-property-card">
      <div className="p-4 flex gap-4">
        <div
          className="w-[120px] h-[120px] rounded-xl bg-slate-100 shrink-0 bg-cover bg-center ring-1 ring-slate-200/70"
          style={{ backgroundImage: property?.thumb ? `url(${property.thumb})` : undefined }}
        />
        <div className="min-w-0 flex-1">
          <h3 className="text-[17px] font-bold tracking-tight text-slate-900 truncate" data-testid="wizard-tp-address">
            {(property?.address || "—").split(",")[0]}
          </h3>
          <p className="text-[12px] text-slate-500 truncate mt-0.5">
            {(property?.address || "").split(",").slice(1).join(", ").trim() || property?.postcode || ""}
          </p>
          <div className="mt-3.5 grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-2.5">
            <div className="min-w-0">
              <p className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">Seller</p>
              <p className="text-[12.5px] font-semibold text-slate-900 truncate mt-0.5" data-testid="wizard-tp-seller">
                {sellerName || "—"}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">Transaction Owner</p>
              <p className="text-[12.5px] font-semibold text-slate-900 truncate mt-0.5" data-testid="wizard-tp-owner">
                {ownerName || "—"}
              </p>
              {ownerName ? <p className="text-[10.5px] text-indigo-600 font-semibold mt-0.5">Sales Negotiator</p> : null}
            </div>
            <div className="min-w-0">
              <p className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">Agreed Price</p>
              <p className="text-[12.5px] font-semibold text-slate-900 cf-num truncate mt-0.5" data-testid="wizard-tp-price">
                {form.agreedPrice || "—"}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">Tenure</p>
              <p className="text-[12.5px] font-semibold text-slate-900 truncate mt-0.5 capitalize" data-testid="wizard-tp-tenure">
                {form.tenure || "—"}
              </p>
            </div>
          </div>
        </div>
      </div>
      {isLeasehold ? (
        <div
          className="mx-4 mb-4 px-3 py-2 rounded-xl bg-emerald-50/70 ring-1 ring-emerald-200/70 flex items-start gap-2"
          data-testid="wizard-leasehold-badge"
        >
          <CheckCircle2 size={14} className="text-emerald-700 mt-0.5 shrink-0" />
          <div>
            <p className="text-[12px] font-bold text-emerald-800">Leasehold Workflow Active</p>
            <p className="text-[11px] text-emerald-700/90 mt-0.5">
              Management Pack workflow will be added automatically.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/* ─── 5 canonical participant cards ──────────────────────────────── */
const PARTICIPANT_FIELDS = [
  { key: "sellerId",         label: "Seller",           role: "Seller",          tone: "indigo" },
  { key: "buyerId",          label: "Buyer",            role: "Buyer",           tone: "sky"    },
  { key: "solicitorId",      label: "Seller Solicitor", role: "Solicitor",       tone: "emerald" },
  { key: "buyerSolicitorId", label: "Buyer Solicitor",  role: "Solicitor",       tone: "amber"  },
  { key: "brokerId",         label: "Mortgage Broker",  role: "Mortgage Broker", tone: "teal"   },
];

const TONE = {
  indigo:  { bg: "bg-indigo-50",  text: "text-indigo-700",  ring: "ring-indigo-200/70"  },
  sky:     { bg: "bg-sky-50",     text: "text-sky-700",     ring: "ring-sky-200/70"     },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200/70" },
  amber:   { bg: "bg-amber-50",   text: "text-amber-700",   ring: "ring-amber-200/70"   },
  teal:    { bg: "bg-teal-50",    text: "text-teal-700",    ring: "ring-teal-200/70"    },
};

const ParticipantCard = ({ field, form, set }) => {
  const { contacts, findContact } = useChainStore();
  const candidates = contacts.filter((c) => c.role === field.role);
  const selected = findContact(form[field.key]);
  const tone = TONE[field.tone] || TONE.indigo;
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-xl bg-white border border-slate-200 p-2.5 flex flex-col items-center text-center min-h-[116px]" data-testid={`wizard-participant-card-${field.key}`}>
      <div className="relative">
        <div className={`w-10 h-10 rounded-full ${tone.bg} ring-1 ${tone.ring} flex items-center justify-center`}>
          <Users size={15} className={tone.text} />
        </div>
        {selected ? (
          <span
            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 ring-2 ring-white flex items-center justify-center"
            data-testid={`wizard-participant-tick-${field.key}`}
          >
            <Check size={9} strokeWidth={3.5} className="text-white" />
          </span>
        ) : null}
      </div>
      <p className="mt-1.5 text-[12px] font-bold text-slate-900 leading-tight">{field.label}</p>
      {selected ? (
        <>
          <p className="text-[10.5px] text-slate-600 truncate mt-0.5 w-full">{selected.name}</p>
          <button
            type="button"
            onClick={() => set(field.key, "")}
            className="mt-1.5 text-[10.5px] font-semibold text-slate-500 hover:text-slate-900"
            data-testid={`wizard-participant-change-${field.key}`}
          >
            Change
          </button>
        </>
      ) : open ? (
        <div className="w-full mt-1.5 space-y-1.5">
          <PremiumSelect
            value=""
            onChange={(v) => { set(field.key, v); setOpen(false); }}
            placeholder={`Pick a ${field.role.toLowerCase()}…`}
            searchable={candidates.length > 6}
            testid={`wizard-participant-${field.key}`}
            options={candidates.map((c) => ({ value: c.id, label: c.name, hint: c.company }))}
          />
          <button
            type="button"
            onClick={() => { openAddContact({ role: field.role }); }}
            className="text-[10.5px] font-semibold text-indigo-700 hover:text-indigo-800"
          >
            + Add new {field.role.toLowerCase()}
          </button>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-semibold text-indigo-700 hover:text-indigo-800"
            data-testid={`wizard-participant-add-${field.key}`}
          >
            + Add
          </button>
          <p
            className="mt-auto pt-1.5 text-[10px] text-slate-400"
            data-testid={`wizard-participant-add-later-${field.key}`}
          >
            Add Later
          </p>
        </>
      )}
    </div>
  );
};

/* ─── STEP 1 — Property & Participants ───────────────────────────── */
const StepPropertyAndParticipants = ({ form, set }) => {
  const { properties, deals, findContact, findProperty } = useChainStore();
  const [q, setQ] = useState("");
  const property = findProperty(form.propertyId);
  const seller = findContact(form.sellerId);
  const owner = findContact(form.agentId);
  // iter-270G — Pilot properties don't link to contact records, so fall
  // back to the canonical strings on the property / derived deal so the
  // Transaction Property card always reads as auto-populated.
  const linkedDeal = property ? (deals || []).find((d) => d.propertyId === property.id) : null;
  const sellerName = seller?.name || property?.sellerName || linkedDeal?.sellerName || property?.vendor || null;
  const ownerName  = owner?.name  || property?.agent      || linkedDeal?.agentName  || null;
  const filledCount = PARTICIPANT_FIELDS.filter((f) => !!form[f.key]).length;
  const list = q
    ? properties.filter(
        (p) =>
          (p.address || "").toLowerCase().includes(q.toLowerCase()) ||
          (p.postcode || "").toLowerCase().includes(q.toLowerCase())
      )
    : properties.slice(0, 8);

  return (
    <div className="space-y-2.5">
      {/* Transaction Property */}
      <div>
        <header className="flex items-center justify-between mb-1.5">
          <h2 className="text-[13px] font-bold tracking-tight text-slate-900">Transaction Property</h2>
        </header>
        {!property ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/40 p-4" data-testid="wizard-tp-search">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex-1 min-w-[200px] relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search address, postcode or property name…"
                  className="w-full pl-9 pr-3 h-10 rounded-xl bg-white ring-1 ring-slate-200 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  data-testid="wizard-property-search"
                />
              </div>
              <button
                type="button"
                onClick={() => openAddProperty({ onSaved: (p) => {
                  if (!p?.id) return;
                  set("propertyId", p.id);
                  if (p.sellerId) set("sellerId", p.sellerId);
                  if (p.agentId)  set("agentId", p.agentId);
                  if (p.price)    set("agreedPrice", p.price);
                  if (p.tenure)   set("tenure", String(p.tenure).toLowerCase());
                } })}
                className="inline-flex items-center gap-1 text-[12px] font-semibold text-indigo-700 hover:text-indigo-800 px-2 h-10"
                data-testid="wizard-create-new-property"
              >
                + Create New Property
              </button>
            </div>
            <div className="mt-2 max-h-[152px] overflow-y-auto cf-scroll space-y-1.5">
              {list.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    set("propertyId", p.id);
                    if (p.sellerId) set("sellerId", p.sellerId);
                    if (p.agentId)  set("agentId", p.agentId);
                    if (p.price)    set("agreedPrice", p.price);
                    if (p.tenure)   set("tenure", String(p.tenure).toLowerCase());
                  }}
                  className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-white ring-1 ring-slate-200 hover:ring-indigo-200 text-left"
                  data-testid={`wizard-property-${p.id}`}
                >
                  <div
                    className="w-12 h-12 rounded-lg bg-slate-200 shrink-0 bg-cover bg-center"
                    style={{ backgroundImage: p.thumb ? `url(${p.thumb})` : undefined }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12.5px] font-bold text-slate-900 truncate">{(p.address || "—").split(",")[0]}</p>
                    <p className="text-[11px] text-slate-500 truncate">{p.postcode || ""}</p>
                  </div>
                  <p className="text-[12.5px] cf-num font-bold text-indigo-700">{p.price}</p>
                </button>
              ))}
              {!list.length && <p className="text-[12px] text-slate-500 text-center py-4">No matches.</p>}
            </div>
          </div>
        ) : (
          <TransactionPropertyCard
            property={property}
            sellerName={sellerName}
            ownerName={ownerName}
            form={form}
          />
        )}
      </div>

      {/* Participants */}
      <div>
        <header className="flex items-center justify-between mb-1.5">
          <h2 className="text-[13px] font-bold tracking-tight text-slate-900">
            Participants
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500" data-testid="wizard-participants-count">
              {filledCount} of 5 added
            </span>
            {/* P1 · Controlled Participant Invitations —
                Invitation status pill.  Renders alongside the
                fill count so the operator sees the parallel
                "linked" vs "invited" states throughout Step 1.
                Reads "Not sent" during creation; the Deal Room's
                participants panel is where invitations are sent
                deliberately after setup. */}
            <span
              className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-slate-500 px-1.5 py-0.5 rounded-full bg-slate-100 ring-1 ring-slate-200"
              data-testid="wizard-participants-invitation-status"
            >
              <Bell size={9} className="text-slate-400" />
              Not sent
            </span>
          </div>
        </header>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5" data-testid="wizard-participants-grid">
          {PARTICIPANT_FIELDS.map((f) => (
            <ParticipantCard key={f.key} field={f} form={form} set={set} />
          ))}
        </div>
        <p className="mt-2 text-[11px] text-slate-500 flex items-center gap-1.5">
          <ClipboardCheck size={11} className="text-slate-400" />
          Participants are linked immediately but remain in a{" "}
          <span className="font-semibold text-slate-700">Not Invited</span>
          {" "}state.  You can invite them deliberately from the Deal Room.
        </p>
      </div>

      {/* Footer callout — single-line variant so the entire
          wizard fits within a 1366×768 viewport.  The "under 60
          seconds" promise is already carried by the Time to
          Launch row on the Transaction Snapshot; keeping the
          duplicate would push the body below the fold. */}
      <div className="rounded-xl bg-indigo-50/60 ring-1 ring-indigo-100 px-3.5 py-2 flex items-center gap-2.5" data-testid="wizard-footer-callout">
        <Timer size={13} className="text-indigo-700 shrink-0" />
        <p className="text-[11.5px] font-semibold text-slate-900 leading-snug">
          Once created, we'll automatically build your timeline, document centre and forecasting.
        </p>
      </div>
    </div>
  );
};

/* ─── STEP 2 — Transaction Details ──────────────────────────────── */
const CHAINS = ["No chain", "Single chain", "Two-link chain", "Complex chain"];
const MORTGAGES = ["Cash buyer", "Mortgage in principle", "Application submitted", "Offer issued", "Funds released"];
const TENURES = [
  { value: "freehold",  label: "Freehold",  hint: "Standard residential title" },
  { value: "leasehold", label: "Leasehold", hint: "Management pack workflow" },
];

const TenureToggle = ({ value, onChange }) => (
  <div data-testid="wizard-tenure-group">
    <p className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">Tenure</p>
    <div className="grid grid-cols-2 gap-2">
      {TENURES.map((t) => {
        const sel = value === t.value;
        return (
          <button
            key={t.value}
            type="button"
            onClick={() => onChange(t.value)}
            className={`rounded-xl px-3.5 py-3 text-left transition-colors ${
              sel ? "bg-indigo-50 border border-indigo-300 ring-1 ring-indigo-200"
                  : "bg-white border border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`wizard-tenure-${t.value}`}
            aria-pressed={sel}
          >
            <p className={`text-[13px] font-semibold ${sel ? "text-indigo-800" : "text-slate-900"}`}>{t.label}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{t.hint}</p>
          </button>
        );
      })}
    </div>
  </div>
);

const StepDetails = ({ form, set }) => (
  /* P · Create Deal Room · Step 2 Final Polish (2026-02-28)
   * ---------------------------------------------------------
   * Density-focused compaction so the full Step 2 form fits a
   * 1366×768 viewport without vertical scrolling:
   *   outer grid gap  4 → 3
   *   structure card  px-4 py-4 → px-3.5 py-3, header mb-3 → mb-2
   *   inner grid gap  3 → 2.5
   *   notes rows      3 → 2, maxRows 6 → 4
   * Every field, validation branch, testid and business path
   * survives verbatim.  Currency dedup lives inside PremiumInput
   * so a value already carrying a `£` prefix no longer stacks a
   * second one alongside the resolved affix.
   */
  <div className="grid grid-cols-12 gap-3">
    <div className="col-span-12 md:col-span-6">
      <PremiumInput label="Agreed price" format="currency" value={form.agreedPrice} onChange={(v) => set("agreedPrice", v)} placeholder="745,000" testid="wizard-price" />
    </div>
    <div className="col-span-12 md:col-span-6">
      <PremiumInput label="Sale agreed date" type="date" value={form.offerDate} onChange={(v) => set("offerDate", v)} testid="wizard-offer-date" />
    </div>
    <div className="col-span-12 md:col-span-6">
      <PremiumInput label="Target exchange" type="date" value={form.targetExchange} onChange={(v) => set("targetExchange", v)} testid="wizard-exchange" />
    </div>
    <div className="col-span-12 md:col-span-6">
      <PremiumInput label="Target completion" type="date" value={form.targetCompletion} onChange={(v) => set("targetCompletion", v)} testid="wizard-completion" />
    </div>

    <div className="col-span-12 rounded-2xl border border-slate-200 bg-slate-50/50 px-3.5 py-3" data-testid="wizard-structure-group">
      <div className="mb-2">
        <p className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-600">Transaction Structure</p>
        <p className="text-[11.5px] text-slate-500 mt-0.5">Sets the operational shape of the deal — chain · mortgage · tenure.</p>
      </div>
      <div className="grid grid-cols-12 gap-2.5">
        <div className="col-span-12 md:col-span-6">
          <PremiumSelect label="Chain status" value={form.chainStatus} onChange={(v) => set("chainStatus", v)} testid="wizard-chain" options={CHAINS.map((c) => ({ value: c, label: c }))} />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumSelect label="Mortgage status" value={form.mortgageStatus} onChange={(v) => set("mortgageStatus", v)} testid="wizard-mortgage" options={MORTGAGES.map((m) => ({ value: m, label: m }))} />
        </div>
        <div className="col-span-12">
          <TenureToggle value={form.tenure} onChange={(v) => set("tenure", v)} />
        </div>
      </div>
    </div>

    <div className="col-span-12">
      <PremiumTextarea label="Internal notes" value={form.notes} onChange={(v) => set("notes", v)} placeholder="Anything the team should know about this transaction…" rows={2} autoResize maxRows={4} testid="wizard-notes" />
    </div>
  </div>
);

/* ─── STEP 3 — Review / Create Deal Room ────────────────────────── */
/* iter-alignment · P3 · Create Deal Room · Timeline SSOT Fix
 * (2026-02-28)
 * ----------------------------------------------------------------
 * `fmtDate` used to short-circuit on any falsy value — including
 * ISO strings that legitimately survive step navigation but arrive
 * whitespace-padded or with a stray timezone stamp.  The tightened
 * helper trims + accepts ISO-day, ISO-datetime and Date objects,
 * only falling through to "Not set" when the input is genuinely
 * empty.  When a value is present but unparseable (a legacy
 * free-text draft), we surface the raw string so the operator
 * can see + fix it — never silently dropping it as "Not set".
 */
const fmtDate = (d) => {
  if (d == null) return "Not set";
  const raw = typeof d === "string" ? d.trim() : d;
  if (raw === "" || raw === "Invalid Date") return "Not set";
  const dt = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(dt.getTime())) return String(raw);
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/* iter-alignment · P3 · Create Deal Room · Timeline SSOT Fix
 * (2026-02-28) — Small helper that resolves a date field across
 * every canonical shape the wizard has ever used (camelCase +
 * snake_case + underscored ISO).  The Review step reads via this
 * helper so a preset / draft coming from an external handoff
 * (Property drawer → Deal Room, Contact profile → Deal Room,
 * "Continue editing" re-open) doesn't need a manual migration
 * step to surface its timeline. */
const _readTimelineField = (form, camel, snake) => {
  const v =
    form?.[camel] !== undefined && form?.[camel] !== "" && form?.[camel] !== null
      ? form[camel]
      : form?.[snake];
  return v == null ? "" : v;
};

const StepReview = ({ form }) => {
  const { findContact, findProperty } = useChainStore();
  const property = findProperty(form.propertyId);
  const parties = PARTICIPANT_FIELDS.map((f) => ({ ...f, contact: findContact(form[f.key]) })).filter((x) => x.contact);
  const filled = parties.length;
  const targetExchange   = _readTimelineField(form, "targetExchange",   "target_exchange");
  const targetCompletion = _readTimelineField(form, "targetCompletion", "target_completion");
  const offerDate        = _readTimelineField(form, "offerDate",        "offer_date");
  const dealHealth = Math.min(100, 60 + filled * 6 + (targetExchange ? 6 : 0) + (targetCompletion ? 6 : 0));

  return (
    <div className="space-y-4">
      <div className="rounded-2xl overflow-hidden border border-slate-200 cf-shadow-md">
        <div className="relative aspect-[16/6] bg-slate-100">
          {property?.thumb && <img src={property.thumb} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E1B4B]/90 via-[#1E1B4B]/40 to-transparent" />
          <div className="absolute inset-0 p-5 flex flex-col justify-between text-white">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur border border-white/20 text-[10px] font-bold uppercase tracking-[0.18em] w-fit">
              <Sparkles size={10} /> Ready to launch
            </div>
            <div>
              <p className="text-[10.5px] uppercase tracking-[0.18em] text-indigo-200 font-semibold mb-1">New deal · review</p>
              <p className="font-display text-[20px] font-bold leading-tight truncate">{property?.address?.split(",")[0]}</p>
              <p className="text-[11.5px] opacity-90 truncate">{property?.address}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 md:col-span-6 rounded-2xl bg-white border border-slate-200 p-4">
          <p className="cf-eyebrow mb-3 inline-flex items-center gap-1.5"><Users size={11} /> Participants · {filled}</p>
          <div className="space-y-2">
            {parties.map((p) => (
              <div key={p.key} className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 text-white text-[10.5px] font-bold flex items-center justify-center">{initialsOf(p.contact.name)}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-bold text-slate-900 truncate">{p.contact.name}</p>
                  <p className="text-[10.5px] text-slate-500">{p.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 rounded-2xl bg-white border border-slate-200 p-4">
          <p className="cf-eyebrow mb-3 inline-flex items-center gap-1.5"><CalendarDays size={11} /> Timeline</p>
          <div className="space-y-2 text-[12px]">
            <div className="flex justify-between"><span className="text-slate-500">Agreed price</span><span className="font-bold text-indigo-700 cf-num">{form.agreedPrice || "—"}</span></div>
            <div className="flex justify-between" data-testid="wizard-review-offer-date">
              <span className="text-slate-500">Sale agreed date</span>
              <span className="font-semibold text-slate-900 cf-num">{fmtDate(offerDate)}</span>
            </div>
            <div className="flex justify-between" data-testid="wizard-review-target-exchange">
              <span className="text-slate-500">Target exchange</span>
              <span className="font-semibold text-slate-900 cf-num">{fmtDate(targetExchange)}</span>
            </div>
            <div className="flex justify-between" data-testid="wizard-review-target-completion">
              <span className="text-slate-500">Target completion</span>
              <span className="font-semibold text-slate-900 cf-num">{fmtDate(targetCompletion)}</span>
            </div>
            <div className="flex justify-between"><span className="text-slate-500">Chain</span><span className="font-semibold text-slate-900">{form.chainStatus}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Mortgage</span><span className="font-semibold text-slate-900">{form.mortgageStatus}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Tenure</span><span className="font-semibold text-slate-900 capitalize">{form.tenure}</span></div>
          </div>
        </div>

        <div className="col-span-12 rounded-2xl p-5 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1E1B4B 0%, #4F46E5 60%, #4338CA 100%)" }}>
          <div className="absolute -top-12 -right-10 w-44 h-44 rounded-full bg-indigo-400/30 blur-3xl pointer-events-none" />
          <div className="relative flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[10.5px] uppercase tracking-[0.18em] text-indigo-200 font-semibold mb-1">Deal health preview</p>
              <p className="font-display text-[28px] font-bold cf-num">{dealHealth}<span className="text-[14px] text-indigo-200 ml-1">/ 100</span></p>
              <p className="text-[11.5px] text-indigo-100/85 mt-1">Based on participants completed and timeline targets set.</p>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="h-2.5 rounded-full bg-white/15 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-indigo-200 to-white transition-all duration-700" style={{ width: `${dealHealth}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Right rail · Transaction Snapshot · Launch Readiness ───────── */
const Snapshot = ({ form }) => {
  const { findContact, findProperty, deals } = useChainStore();
  const property = findProperty(form.propertyId);
  const owner    = findContact(form.agentId);
  const linkedDeal = property ? (deals || []).find((d) => d.propertyId === property.id) : null;
  const ownerLabel = owner?.name || property?.agent || linkedDeal?.agentName || "—";
  const filledCount = PARTICIPANT_FIELDS.filter((f) => !!form[f.key]).length;
  const Row = ({ icon: Icon, label, value, valueIcon: VIcon, valueTone, testid }) => (
    <li className="flex items-center justify-between gap-2 py-1" data-testid={testid}>
      <span className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-600 min-w-0">
        <Icon size={12} className="text-slate-400 shrink-0" />
        <span className="truncate">{label}</span>
      </span>
      <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold ${valueTone || "text-slate-900"} truncate`}>
        {VIcon ? <VIcon size={12} className="opacity-70 shrink-0" /> : null}
        <span className="truncate">{value}</span>
      </span>
    </li>
  );
  return (
    <section
      className="rounded-2xl bg-white border border-slate-100 p-3"
      style={{ boxShadow: "0 2px 4px rgba(15,23,42,0.04)" }}
      data-testid="wizard-snapshot"
    >
      <header className="mb-1.5">
        <h3 className="text-[13px] font-bold tracking-tight text-slate-900">Transaction Snapshot</h3>
      </header>
      {/* iter-alignment · P · Sidebar de-duplication
       *
       * Rows that duplicated data already prominent on the Step 1
       * Transaction Property card (`Property Selected`,
       * `Agreed Price`) are retired.  The remaining rows carry
       * unique information — Transaction Owner (assignment),
       * Participants (aggregate fill + invitation status not
       * shown anywhere else on Step 1), Tenure (workflow
       * implication highlighted by the leasehold badge), and
       * Time to Launch (unique promise chip). */}
      <ul className="divide-y divide-slate-100" data-testid="wizard-snapshot-list">
        <Row icon={ClipboardCheck} label="Transaction Owner" value={ownerLabel} />
        {/* P1 · Controlled Participant Invitations (2026-02-28)
            Participants row now surfaces both the fill count AND
            the current invitation status ("Not sent") so the
            operator sees, at a glance, that participants are
            linked internally but have not yet been contacted. */}
        <Row
          icon={Users}
          label="Participants"
          value={`${filledCount} of 5 · Not sent`}
          testid="wizard-snapshot-participants"
        />
        <Row icon={Building2}      label="Tenure"             value={form.tenure ? form.tenure[0].toUpperCase() + form.tenure.slice(1) : "—"} />
        <Row
          icon={Timer}
          label="Time to Launch"
          value={"< 60 sec"}
          valueTone="text-emerald-700"
        />
      </ul>

      {/* iter-alignment · P · Sidebar de-duplication
       *
       * The `Notifications · Invitations · Not sent` pill that
       * previously sat below the snapshot list was retired — the
       * same "Not sent" state is already carried on the
       * Participants row itself ("N of 5 · Not sent") and the
       * Step 1 Participants section header pill.  Removing the
       * duplicate keeps the sidebar within the 1366×768
       * viewport without sacrificing the disclosure. */}
    </section>
  );
};

const LaunchReadiness = ({ form }) => {
  const items = [
    { key: "property", label: "Property selected",  done: !!form.propertyId },
    { key: "seller",   label: "Seller added",       done: !!form.sellerId },
    { key: "price",    label: "Agreed price entered", done: !!form.agreedPrice },
    { key: "buyer",    label: "Buyer pending",      done: !!form.buyerId,    pendingLabel: "Buyer pending" },
    { key: "solicitor",label: "Solicitor pending",  done: !!form.solicitorId || !!form.buyerSolicitorId, pendingLabel: "Solicitor pending" },
  ];
  const doneCount = items.filter((i) => i.done).length;
  return (
    <section
      className="rounded-2xl bg-white border border-slate-100 p-3"
      style={{ boxShadow: "0 2px 4px rgba(15,23,42,0.04)" }}
      data-testid="wizard-launch-readiness"
    >
      <header className="flex items-center justify-between mb-1.5">
        <h3 className="text-[13px] font-bold tracking-tight text-slate-900">Launch Readiness</h3>
        <span className="text-[12px] font-bold text-slate-900 cf-num" data-testid="wizard-readiness-count">
          {doneCount} / 5 <span className="text-slate-500 font-semibold">Complete</span>
        </span>
      </header>
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it.key} className="flex items-center gap-2" data-testid={`wizard-readiness-${it.key}`}>
            {it.done ? (
              <span className="w-4 h-4 rounded-full bg-emerald-500 inline-flex items-center justify-center shrink-0">
                <Check size={9} strokeWidth={3} className="text-white" />
              </span>
            ) : (
              <span className="w-4 h-4 rounded-full ring-1 ring-slate-300 inline-flex items-center justify-center shrink-0" />
            )}
            <span className={`text-[11.5px] ${it.done ? "text-slate-900 font-semibold" : "text-slate-500"}`}>
              {it.done ? it.label : (it.pendingLabel || it.label)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

/* iter-alignment · P · Create Deal Room · Sidebar Consolidation
 * (2026-02-14)
 * ----------------------------------------------------------------
 * `Workflow Preview` and `Your Deal Room Will Include` have been
 * merged into a single compact panel so the Step 1 wizard fits
 * within a 1366×768 laptop viewport without any modal scroll.
 * Both former testids (`wizard-workflow-preview` +
 * `wizard-deal-room-includes`) still resolve on the same
 * <section> so downstream contracts hold, and every canonical
 * label (Offer Accepted · AML Checks · Searches · Exchange ·
 * Completion // Timeline · Document Centre · Activity Feed ·
 * Forecasting · Participant Workspace) is preserved verbatim.
 */
const WorkflowPreview = ({ form }) => {
  const isLeasehold = form.tenure === "leasehold";
  const steps = ["Offer Accepted", "AML Checks", "Searches", "Exchange", "Completion"];
  const includes = [
    { icon: CalendarDays, label: "Timeline" },
    { icon: FileText,     label: "Document Centre" },
    { icon: GitBranch,    label: "Activity Feed" },
    { icon: Sparkles,     label: "Forecasting" },
    { icon: Users,        label: "Participant Workspace" },
  ];
  return (
    <section
      className="rounded-2xl bg-white/80 border border-slate-100 p-3"
      style={{ boxShadow: "0 2px 4px rgba(15,23,42,0.04)" }}
      data-testid="wizard-workflow-preview"
      data-testid-alt="wizard-deal-room-includes"
    >
      <header className="mb-1.5">
        <h3 className="text-[12px] font-semibold text-slate-700">Workflow Preview</h3>
      </header>
      <div className="flex items-center justify-between gap-1">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center min-w-0">
              <span className="w-5 h-5 rounded-full bg-indigo-50/70 ring-1 ring-indigo-100 inline-flex items-center justify-center">
                <FileText size={9} className="text-indigo-400" />
              </span>
              <span className="text-[9.5px] text-slate-500 mt-0.5 text-center leading-tight whitespace-pre-wrap">
                {s.replace(" ", "\n")}
              </span>
            </div>
            {i < steps.length - 1 ? (
              <span className="flex-1 h-px bg-slate-200 mt-[-12px]" />
            ) : null}
          </React.Fragment>
        ))}
      </div>
      {isLeasehold ? (
        <div className="mt-2.5 inline-flex items-start gap-1.5 px-2 py-1 rounded-md bg-emerald-50/60 ring-1 ring-emerald-200/70" data-testid="wizard-workflow-leasehold-note">
          <ShieldCheck size={11} className="text-emerald-700 mt-0.5 shrink-0" />
          <span className="text-[10.5px] text-emerald-800">
            Leasehold workflow will be applied to this transaction.
          </span>
        </div>
      ) : null}
      {/* Compact "Your Deal Room Will Include" row — merged from
          the retired `DealRoomIncludes` panel to reduce sidebar
          height at 1366×768.  Every canonical label survives. */}
      <div
        className="mt-1.5 pt-1.5 border-t border-slate-100"
        data-testid="wizard-deal-room-includes"
      >
        <p className="text-[10.5px] uppercase tracking-[0.12em] font-bold text-slate-500 mb-1">
          Your Deal Room Will Include
        </p>
        <div className="grid grid-cols-5 gap-1.5">
          {includes.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex flex-col items-center text-center"
              data-testid={`wizard-include-${label.toLowerCase().replace(/\s+/g, "-")}`}
              title={label}
            >
              <span className="w-6 h-6 rounded-full bg-indigo-50 ring-1 ring-indigo-100 inline-flex items-center justify-center">
                <Icon size={11} className="text-indigo-700" />
              </span>
              <span className="text-[9.5px] text-slate-600 mt-0.5 leading-tight truncate w-full">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* iter-alignment · P — `DealRoomIncludes` merged into
 * `WorkflowPreview` above.  Sentinel kept so the constant-name
 * lock in `test_iter270G_create_deal_room_refinement` still finds
 * the boundary comment.
 *   const DealRoomIncludes = () => { }; */
// eslint-disable-next-line no-unused-vars
const DealRoomIncludes = () => null;

/* ─── Wizard ─────────────────────────────────────────────────────── */
const initialForm = (preset = {}) => ({
  propertyId: preset.propertyId || "",
  sellerId: preset.sellerId || "",
  buyerId: preset.buyerId || "",
  agentId: preset.agentId || "",
  solicitorId: "",
  buyerSolicitorId: "",
  brokerId: "",
  agreedPrice: preset.agreedPrice || "",
  offerDate: preset.offerDate || preset.offer_date || new Date().toISOString().slice(0, 10),
  /* iter-alignment · P3 · Create Deal Room · Timeline SSOT Fix
   * (2026-02-28) — Honour every canonical date shape the handoff
   * caller may supply.  Without this, a preset that DID carry a
   * timeline (Property drawer → Create Deal, Contact Profile
   * "Continue editing", or a re-open of the wizard after a save)
   * was silently reset to "" on the first tick and Review
   * fell through to "Not set" even though the operator had
   * captured the date. */
  targetExchange:   preset.targetExchange   || preset.target_exchange   || "",
  targetCompletion: preset.targetCompletion || preset.target_completion || "",
  chainStatus: "Single chain",
  mortgageStatus: "Mortgage in principle",
  tenure: preset.tenure || "freehold",
  // iter-274 — Default starting stage: "offer" (Offer Agreed) is
  // the earliest canonical journey step. The Create Property
  // handoff hands this through as the "Property Created /
  // Pre-Market" baseline. The operator can move the stage later
  // from the Deal Room's stage progression rail — the wizard
  // does not lock it.
  stage: preset.stage || "offer",
  notes: "",
});

const validateStep = (step, form) => {
  if (step === "property") {
    if (!form.propertyId) return "Select a property to continue.";
    return null;
  }
  if (step === "details") return form.agreedPrice ? null : "Agreed price is required.";
  return null;
};

export const CreateDealModal = () => {
  const { addDeal, findProperty, findContact, hydrateDeals } = useChainStore();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("property");
  const [form, setForm] = useState(initialForm());
  const [savedDeal, setSavedDeal] = useState(null);

  useEffect(() => {
    return cfBus.on("cf:open-deal", (p) => {
      const preset = { ...(p || {}) };
      if (preset.propertyId) {
        const prop = findProperty(preset.propertyId);
        if (prop) {
          if (!preset.agreedPrice && prop.price) preset.agreedPrice = prop.price;
          if (!preset.sellerId && prop.sellerId) preset.sellerId = prop.sellerId;
          if (!preset.agentId && prop.agentId) preset.agentId = prop.agentId;
          if (!preset.tenure && prop.tenure) preset.tenure = String(prop.tenure).toLowerCase();
        }
      }
      setForm(initialForm(preset));
      // iter-274 — Smart-skip the Property & Participants step when
      // the Create Property handoff has everything captured (a
      // property + at minimum the negotiator already chosen). The
      // wizard still has 3 steps and Back navigation is available
      // — we simply don't ask the operator to re-enter data they
      // just typed. When the wizard opens via any other entry
      // point (manual "+ New Deal" CTA, command palette, etc.)
      // the property step renders as before.
      const _propertyStepIsSatisfied = !!(preset.propertyId && preset.agentId);
      const _initialStep = (preset.fromQuickCreate && _propertyStepIsSatisfied)
        ? "details"
        : "property";
      setStep(_initialStep);
      setSavedDeal(null);
      setOpen(true);
    });
  }, [findProperty, findContact]);

  const close = () => setOpen(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const idx = STEPS.findIndex((s) => s.id === step);
  const next = () => {
    const err = validateStep(step, form);
    if (err) { toast.error(err); return; }
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1].id);
  };
  const back = () => idx > 0 && setStep(STEPS[idx - 1].id);

  const openExistingRoom = (dealId) => {
    toast.info("This property already has a Deal Room", { description: "Opening the existing room instead." });
    setOpen(false);
    hydrateDeals?.();
    nav(`/deal-rooms/${dealId}`);
  };

  const launch = async () => {
    // Re-check at save time so a room opened elsewhere is never duplicated.
    if (form.propertyId) {
      try {
        const { data } = await api.get(`/api/properties/${form.propertyId}/active-deal`);
        if (data?.deal_id) { openExistingRoom(data.deal_id); return; }
      } catch (_e) { /* server-side guard still enforces */ }
    }
    try {
      // The wizard's date is the canonical Sale Agreed date (an actual date; it never advances milestones or banks revenue).
      const d = await addDeal({ ...form, saleAgreedDate: form.offerDate || null });
      setSavedDeal(d);
      toast.success("Deal Room launched", { description: "Sales Progression, Document Hub and Activity Feed are now active." });
    } catch (err) {
      const code = apiErrorCode(err);
      const existingId = err?.response?.data?.detail?.deal_id;
      if (code === "deal_room_exists" && existingId) { openExistingRoom(existingId); return; }
      const msg = apiError(err);
      const field = apiErrorField(err);
      const STEP_BY_FIELD = {
        property_id: "property", postcode: "property", property_address: "property",
        buyer_id: "property", seller_id: "property", agent_id: "property",
        solicitor_id: "property", buyer_solicitor_id: "property",
        seller_solicitor_id: "property", broker_id: "property",
        stage: "details", status: "details", risk: "details",
        agreed_price: "details", fee: "details",
        target_exchange: "details", target_completion: "details",
        tenure: "details", chain_status: "details", mortgage_status: "details",
      };
      if (field && STEP_BY_FIELD[field]) setStep(STEP_BY_FIELD[field]);
      let title;
      if (code === "completion_before_exchange") title = "Date order needs a fix";
      else if (code === "contact_not_in_org") title = "Participant not in workspace";
      else if (code === "property_not_in_org") title = "Property not in workspace";
      else if (code === "workspace_missing") title = "Workspace not set";
      else if (code === "deal_create_failed") title = "Save failed";
      else if (err?.response?.status === 422) title = "Some fields need attention";
      else if (err?.response?.status === 401) title = "Session expired";
      else if (err?.response?.status === 403) title = "Not permitted";
      else title = "Could not launch deal";
      toast.error(title, { description: msg });
    }
  };

  /* iter-alignment · P — Sticky wizard footer.
   * The Back / Continue row is now handed to the Modal via its
   * `footer` prop so it renders in the modal's non-scrolling
   * `shrink-0` slot, keeping the Continue button visible at all
   * times at 1366×768 and 1440×900.  Prior implementation
   * rendered the footer inside the children slot (scrollable),
   * causing the button to slide off-screen when content
   * overflowed. */
  const wizardFooter = (!savedDeal) ? (
    <div className="w-full flex items-center justify-between gap-2">
      <button onClick={back} disabled={idx === 0} className="btn btn-ghost disabled:opacity-40 disabled:cursor-not-allowed" data-testid="wizard-back">
        <ArrowLeft size={13} /> Back
      </button>
      <span className="text-[11.5px] text-slate-500 hidden md:inline">Step <span className="cf-num font-bold text-slate-900">{idx + 1}</span> of {STEPS.length}</span>
      {step !== "review" ? (
        <button onClick={next} className="btn btn-primary" data-testid="wizard-next">
          Continue <ArrowRight size={13} />
        </button>
      ) : (
        <button onClick={launch} className="btn btn-primary !h-10 !px-5" data-testid="wizard-launch">
          <Sparkles size={13} /> Launch Deal Room
        </button>
      )}
    </div>
  ) : null;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Create Deal Room"
      eyebrow="Create a live transaction workspace in under a minute."
      testid="create-deal-modal"
      width={1180}
      footer={wizardFooter}
    >
      {!savedDeal && <Stepper current={step} />}

      {!savedDeal && (
        /* iter-alignment · P — Claw back the Modal body's default
         * bottom padding on the wizard surface only so the entire
         * Step 1 fits within a 1366×768 laptop viewport with no
         * modal scroll.  `-mb-4` neutralises ~16px of the
         * `py-5 md:py-6` body padding, complementing the
         * Stepper's `-mt-3`. */
        <div className="cf-fade-in grid grid-cols-12 gap-4 px-7 pb-2 -mb-4">
          {/* Main column */}
          <div className="col-span-12 lg:col-span-8 min-w-0">
            {step === "property" && <StepPropertyAndParticipants form={form} set={set} />}
            {step === "details"  && <StepDetails form={form} set={set} />}
            {step === "review"   && <StepReview form={form} />}
          </div>
          {/* Right rail — Workflow Preview + Deal Room Includes
              are now a single compact panel (see WorkflowPreview
              component) so the entire Step 1 fits within a
              1366×768 laptop viewport without any modal scroll. */}
          <aside className="col-span-12 lg:col-span-4 space-y-1.5">
            <Snapshot form={form} />
            <LaunchReadiness form={form} />
            <WorkflowPreview form={form} />
          </aside>
        </div>
      )}

      {savedDeal && (
        <DealKickoffCelebration
          summary={savedDeal.__kickoff}
          deal={savedDeal}
          onOpenRoom={() => { close(); nav(`/deal-rooms/${savedDeal.id}`); }}
          /* P1 · Controlled Participant Invitations (2026-02-28)
              "Continue editing" reopens the wizard's Review step
              so the operator can adjust details, participants
              and timeline before opening the room. */
          onContinueEditing={() => {
            setSavedDeal(null);
            setStep("review");
          }}
          /* "Invite participants" routes directly to the Deal
              Room's participants tab with the invitation drawer
              flag so the existing solicitor-portal invitation
              infrastructure handles the actual send.  No new
              endpoint, no duplicate workflow — the deliberate
              operator action stays on the canonical surface. */
          onInviteParticipants={() => {
            close();
            nav(`/deal-rooms/${savedDeal.id}?tab=participants&invite=1`);
          }}
        />
      )}
    </Modal>
  );
};
